import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * HeroWordmark — kinetic brand mark.
 *  • Load: each letter rises out of an overflow mask (signature reveal)
 *  • Idle: gentle per-letter float with a unique phase
 *  • Scroll: letters fly outward from center — left half drifts left,
 *    right half drifts right — with rotation, lift and fade.
 */
export default function HeroWordmark({ scrollTargetRef }) {
  const { scrollYProgress } = useScroll({
    target: scrollTargetRef,
    offset: ["start start", "end start"],
  });

  const word = "HueBleu";
  const letters = word.split("");
  const center = (letters.length - 1) / 2; // 3 for 7 letters

  return (
    <h1
      data-testid="hero-wordmark"
      aria-label="HueBleu"
      className="text-white leading-[0.95] font-normal uppercase cursor-default select-none flex justify-center items-baseline"
      style={{
        fontFamily: '"Bebas Neue", "Manrope", sans-serif',
        fontSize: "clamp(80px, 14vw, 240px)",
        letterSpacing: "0.06em",
      }}
    >
      {letters.map((ch, i) => (
        <Letter
          key={i}
          ch={ch}
          index={i}
          distanceFromCenter={i - center}
          maxDistance={center}
          scrollYProgress={scrollYProgress}
        />
      ))}
    </h1>
  );
}

function Letter({ ch, index, distanceFromCenter, maxDistance, scrollYProgress }) {
  // Sign: -1 (left half) | +1 (right half) | 0 (true center)
  const direction = Math.sign(distanceFromCenter);
  // How far this letter travels relative to outermost (0 → 1)
  const intensity = Math.abs(distanceFromCenter) / maxDistance;

  const flyX = useTransform(
    scrollYProgress,
    [0, 0.5],
    [0, direction * (700 + intensity * 800)]
  );
  const flyY = useTransform(scrollYProgress, [0, 0.5], [0, -80 - intensity * 40]);
  const rotZ = useTransform(
    scrollYProgress,
    [0, 0.5],
    [0, direction * (8 + intensity * 14)]
  );
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.5], [1, 0.85, 0]);

  return (
    <motion.span
      style={{
        display: "inline-block",
        x: flyX,
        y: flyY,
        rotateZ: rotZ,
        opacity,
        willChange: "transform, opacity",
      }}
    >
      {/* Entrance mask — letter rises out of a hidden line */}
      <span className="inline-block overflow-hidden align-baseline pb-[0.06em] -mb-[0.06em]">
        <motion.span
          style={{ display: "inline-block", willChange: "transform" }}
          initial={{ y: "112%", rotate: 4 }}
          animate={{ y: "0%", rotate: 0 }}
          transition={{
            duration: 1.2,
            delay: 1.7 + index * 0.06,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <motion.span
            style={{ display: "inline-block" }}
            animate={{ y: [0, -3, 0] }}
            transition={{
              duration: 8 + (index % 3) * 0.7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: (index * 0.25) % 2,
            }}
          >
            {ch === "u" ? "U" : ch === "e" ? "E" : ch}
          </motion.span>
        </motion.span>
      </span>
    </motion.span>
  );
}
